import type { CurrencyRegistry } from "./CurrencyRegistry";
import type { CurrencyDefinition } from "./CurrencyCatalogTypes";

export class MoneyFormatter {
  private readonly currencyCode: string;
  private readonly denominationFactor: number; // e.g. 100 for cents, 1 for already-minor, etc.
  private readonly registry: CurrencyRegistry;

  public constructor(input: {
    currencyCode: string;
    denominationFactor: number;
    registry: CurrencyRegistry;
  }) {
    this.currencyCode = input.currencyCode;
    this.denominationFactor = input.denominationFactor;
    this.registry = input.registry;
  }

  /** Formats a MINOR unit integer string (engine amounts) using the *bound* currency rules. */
  public formatMinor(minorAmount: string, options?: { omitSymbol?: boolean }): string {
    const def = this.getDefinitionOrDefault();

    // minorAmount is integer string (engine)
    const minor = BigInt(minorAmount || "0");

    // Convert to "display units" (major) by denominationFactor
    // Example: denominationFactor=100 => 12345 => 123.45 (depending decimalDigits)
    const factor = BigInt(Math.max(1, Math.floor(this.denominationFactor)));

    const isNegative = minor < 0n;
    const absMinor = isNegative ? -minor : minor;

    const majorInt = absMinor / factor;
    const remainder = absMinor % factor;

    // decimalDigits is how many digits to show (from currency rule)
    const digits = Math.max(0, def.decimalDigits);

    // If factor doesn't match digits (it might, but not guaranteed), we still print digits.
    // We scale remainder into digits by padding/truncation relative to factor.
    let frac = remainder.toString();

    // pad to at least digits based on factor magnitude (best effort)
    // Example factor=100 => remainder "5" => "05"
    const factorDigits = factor.toString().length - 1; // 100 => 2, 1000 => 3
    while (frac.length < factorDigits) frac = "0" + frac;

    // Now match desired digits
    if (digits === 0) {
      frac = "";
    } else if (frac.length > digits) {
      frac = frac.slice(0, digits);
    } else {
      while (frac.length < digits) frac = frac + "0";
    }

    const majorStr = majorInt.toString();
    const numberStr = digits > 0 ? `${majorStr}.${frac}` : majorStr;
    const signed = isNegative ? `-${numberStr}` : numberStr;

    if (options?.omitSymbol) return signed;

    const space = def.spaceBetweenAmountAndSymbol ? " " : "";
    const symbol = def.currencySymbol === "{EMPTY}" ? "" : def.currencySymbol;

    // Hacksaw-style: symbol placement is not in that JSON (they likely have more rules),
    // so we’ll default to prefix symbol when it’s $, €, £ etc and suffix otherwise.
    // You can add an explicit "symbolPosition" later if you want full control.
    const preferPrefix = ["$", "€", "£", "¥"].includes(symbol);

    if (!symbol) return signed;
    return preferPrefix ? `${symbol}${space}${signed}` : `${signed}${space}${symbol}`;
  }

  private getDefinitionOrDefault(): CurrencyDefinition {
    return (
      this.registry.get(this.currencyCode) ?? {
        currencyName: this.currencyCode,
        currencySymbol: this.currencyCode,
        spaceBetweenAmountAndSymbol: true,
        decimalDigits: 2,
      }
    );
  }
}